import React, { useEffect, useState } from 'react';
import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import {
  errorMessage,
  OrderCreationMasterData,
  WeightType,
} from '@helpers/Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { useToast } from '@contexts/Toast';
import {
  OrderServiceProps,
  SpeciesMaster,
  PackageMaster,
  State,
  OrderSpeciesData,
  CustomerProps,
  AddonsListProps,
  MasterProduct,
} from '@helpers/Models';
import { CustomerOrderService } from '@services/CustomerService';
import { Axios } from '@helpers/ApiService';
import {
  calculateTotalPrice,
  handleDateFormat,
  resetCustomerOrderData,
} from '@services/OrderCreationService';

export const ViewOrderSummary: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const orderNumber = state?.orderNumber;
  const { showToastMessage } = useToast();

  const [estCompletionDate, setEstCompletionDate] = useState<string>();
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const [speciesData, setSpeciesData] = useState<OrderSpeciesData[]>();
  const [services, setServices] = useState<OrderServiceProps[]>([]);
  const [currentSpecies, setCurrentSpecies] = useState<SpeciesMaster | null>(
    null,
  );
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [packageMasters, setPackageMasters] = useState<PackageMaster[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [customers, setCustomers] = useState<CustomerProps[]>([]);
  const [customerId, setCustomerId] = useState<number>();
  const [additionalNotes, setAdditionalNotes] = useState<string[]>([]);
  const [totalDeposit, setTotalDeposit] = useState<number>();
  const [advancePayment, setAdvancePayment] = useState<number>();

  const [addons, setAddons] = useState<AddonsListProps[]>([]);
  //To get customer name
  const customer = customers.find((customer) => customer.id === customerId);

  /**
   * Initializes order-related state on component mount.
   *
   * This `useEffect` hook performs the following:
   * - Retrieves parsed order data and customer ID from `CustomerOrderService`.
   * - Extracts species-level data for the current customer.
   * - Calculates and sets the total advance payment and deposit across all species.
   * - Sets the estimated completion date if it exists.
   * - Stores species data in local state.
   * - If a `speciesId` is available, fetches all master data associated with it.
   * - Also extracts and sets arrays of individual `additionalNotes`, `advancePayment`, and `depositAmount` values
   *   for form prepopulation or further use.
   *
   * Dependencies: Runs only once on initial render (`[]`).
   */
  useEffect(() => {
    const parsedOrderData = CustomerOrderService.getParsedOrderData();
    const customerId = CustomerOrderService.getCustomerId();
    setCustomerId(customerId);

    const currentCustomerOrderData = parsedOrderData[customerId];
    const OrderDataSpecies = currentCustomerOrderData.species;
    const totalDeposit = OrderDataSpecies.reduce(
      (
        speciesSum: number,
        species: { services: { depositAmount?: number }[] },
      ) => {
        const serviceDeposits = Array.isArray(species.services)
          ? species.services.reduce(
            (serviceSum: number, service: { depositAmount?: number }) => {
              return service.depositAmount
                ? serviceSum + service.depositAmount
                : serviceSum;
            },
            0,
          )
          : 0;
        return speciesSum + serviceDeposits;
      },
      0,
    );

    setTotalDeposit(totalDeposit);
    if (currentCustomerOrderData.estCompletionDate) {
      setEstCompletionDate(
        handleDateFormat(currentCustomerOrderData.estCompletionDate),
      );
    } else {
      setEstCompletionDate(undefined);
    }
    setSpeciesData(OrderDataSpecies);

    fetchCustomers();

    if (currentCustomerOrderData.speciesId) {
      fetchAddons();
      fetchMasterPackages();
      fetchMasterProducts();
      fetchMasters(currentCustomerOrderData.speciesId);

      const notes = OrderDataSpecies.map(
        (s: { additionalNotes: string }) => s.additionalNotes ?? '',
      );
      setAdditionalNotes(notes);
      setAdvancePayment(currentCustomerOrderData.advancePayment);
    }
  }, []);

  /**
   * Fetches all master data related to a specific species and updates state accordingly.
   *
   * This function retrieves the following master data in parallel:
   * - Services
   * - Species
   *
   * Once the data is fetched, it sets the relevant state variables:
   * - `setServices` with services data.
   * - `setCurrentSpecies` with the species that matches the provided `speciesId`.
   *
   * @param {number} speciesId - The ID of the species to fetch related master data for.
   *
   * @returns {Promise<any>} A promise that resolves when all data is fetched and state is updated.
   */
  const fetchMasterData = async (
    speciesId: number,
    type: number,
  ): Promise<unknown> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        speciesId,
        masterDataType: type,
      });

      return response.data?.data?.masterData;
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
      return undefined;
    }
  };

  /**
   * Fetches the list of master products for species customization.
   *
   * This function makes a POST request to the `FETCH_SPECIES_MASTERDATA` API
   * with `masterDataType` set to 4, which is used to retrieve product master data.
   * The response is then stored in the component state using `setMasterProducts`.
   *
   * In case of an error, a toast message is shown and the error is rethrown.
   *
   * @returns {Promise<void>} A promise that resolves when the data is successfully fetched and set.
   */
  const fetchMasterProducts = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PRODUCT_MASTER_DATA,
      });
      const masterData = response.data.data.masterData;
      setMasterProducts(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the list of available add-ons for product customization.
   *
   * This function sends a POST request to the `FETCH_ADDONS` API endpoint.
   * Upon successful response, it sets the retrieved add-ons into the component state
   * using `setAddons`. The expected response contains an `addOns` array.
   *
   * In case of a request failure, an error toast is displayed and the error is rethrown
   * for further handling.
   *
   * @returns {Promise<void>} A promise that resolves when the add-ons are successfully fetched and set.
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      setAddons(response.data.data.addOns);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the master data for product packaging options.
   *
   * Sends a POST request to the `FETCH_SPECIES_MASTERDATA` endpoint with `masterDataType` set to 6,
   * which corresponds to packaging data. On success, it extracts the `packageType` array from the
   * response and updates the component state via `setPackageMasters`.
   *
   * Displays an error toast and rethrows the error if the request fails.
   *
   * @returns {Promise<void>} A promise that resolves once the packaging data has been successfully fetched and stored.
   */
  const fetchMasterPackages = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      const masterData = response.data.data.masterData.packageType;
      setPackageMasters(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches all master data related to a given species and updates component state accordingly.
   *
   * This function:
   * - Uses `Promise.all` to concurrently fetch master data for:
   *    - Services (typeId: 3)
   *    - Species list (typeId: 1)
   * - Updates the local state with:
   *    - List of available services
   * - Attempts to find and set the current species from the fetched species data.
   *
   * @param {number} speciesId - The ID of the species for which master data is to be fetched.
   */
  const fetchMasters = async (speciesId: number): Promise<void> => {
    try {
      const [servicesData, speciesData] = (await Promise.all([
        fetchMasterData(speciesId, 3),
        fetchMasterData(speciesId, 1),
      ])) as [{ services?: OrderServiceProps[] } | undefined, SpeciesMaster[] | undefined];
      setServices(servicesData?.services ?? []);
      fetchStates();
      const foundSpecies = speciesData?.find(
        (species: SpeciesMaster) => species.id === speciesId,
      );
      setCurrentSpecies(foundSpecies ?? null);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the list of states from the API and updates the local state.
   *
   * Makes a GET request to `ApiEndpoints.FETCH_STATES` and stores the
   * returned state list in `states`. Displays an error toast if the
   * request fails.
   *
   * @async
   * @function
   * @returns {Promise<void>} A promise that resolves when the states are fetched.
   */
  const fetchStates = async (): Promise<void> => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_STATES);
      setStates(response.data.data);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Toggles the visibility of an accordion section based on its index.
   *
   * If the specified index is already in the `openAccordions` state array,
   * it removes it (i.e., closes that accordion section). Otherwise, it adds
   * the index to the array (i.e., opens the section).
   *
   * @param {number} index - The index of the accordion section to toggle.
   *
   * @returns {void}
   */
  const toggleAccordion = (index: number): void => {
    setOpenAccordions((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  /**
   * Fetches a list of customers from the backend API and updates the state.
   *
   * Makes a POST request to the `FETCH_CUSTOMERS` API endpoint and expects
   * a response containing a `customers` array. If valid data is returned,
   * it updates the local `customers` state. Otherwise, shows an error toast.
   *
   * @async
   * @returns {Promise<void>}
   *
   * @throws Will show an error toast and rethrow the error if the request fails.
   */
  const fetchCustomers = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_CUSTOMERS, {});
      const data = response?.data?.data;
      setCustomers(data.customers);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Handles navigation while resetting the customer's order data.
   *
   * This function ensures that any existing customer-specific order data
   * is cleared from local storage before navigating to the specified path.
   * It helps prevent stale or incorrect data from persisting across
   * different navigation flows.
   *
   * @param {string} path - The target route path to navigate to.
   *
   * @returns {void}
   */
  const handleNavigate = (path: string): void => {
    resetCustomerOrderData();
    navigate(path);
  };

  return (
    <div className="flex h-screen w-screen">
      <SideNav active={2} />
      <div className="flex-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        <div className="p-3 pb-16">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Order Summary</h2>
            <div className="flex items-center">
              <button
                type="button"
                className="text-gray-600 hover:text-gray-900 p-2 border border-[#D4D4D4] rounded hover:border-gray-400"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 12L3 7L4.4 5.55L7 8.15V0H9V8.15L11.6 5.55L13 7L8 12ZM2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V11H2V14H14V11H16V14C16 14.55 15.8042 15.0208 15.4125 15.4125C15.0208 15.8042 14.55 16 14 16H2Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div className="mt-4 rounded-md bg-[#DFDFD7] h-[96px]">
            <div className="p-4">
              <div className="flex justify-between items-center bg-[#DFDFD7]">
                <div className="flex items-center space-x-4">
                  <div>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-2">
                        <h2 className="font-semibold text-xl">
                          Order no. {orderNumber}
                        </h2>
                      </div>
                    </div>
                    <p className="mt-1">Order Created by:</p>
                  </div>
                </div>
                <div className="flex justify-end items-center gap-4">
                  <span className="font-semibold text-xl leading-none">
                    {customer?.firstName} {customer?.lastName}
                  </span>
                  <div className="bg-white p-2 rounded shadow-md border border-gray-300">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.666104 2.83594C0.666104 1.45523 1.78539 0.335938 3.1661 0.335938H14.8328C16.2135 0.335938 17.3328 1.45523 17.3328 2.83594V3.65634C17.3329 3.66445 17.3329 3.67256 17.3328 3.68067V11.1693C17.3328 12.55 16.2135 13.6693 14.8328 13.6693H3.1661C1.78539 13.6693 0.666104 12.55 0.666104 11.1693V3.68066C0.665986 3.67256 0.665986 3.66446 0.666104 3.65635V2.83594ZM2.33277 5.22637V11.1693C2.33277 11.6295 2.70587 12.0026 3.1661 12.0026H14.8328C15.293 12.0026 15.6661 11.6295 15.6661 11.1693V5.22637L10.3862 8.74631C9.54644 9.30614 8.45244 9.30614 7.61269 8.74631L2.33277 5.22637ZM15.6661 3.22328L9.46169 7.35956C9.18177 7.54617 8.8171 7.54617 8.53719 7.35956L2.33277 3.22328V2.83594C2.33277 2.3757 2.70587 2.0026 3.1661 2.0026H14.8328C15.293 2.0026 15.6661 2.3757 15.6661 2.83594V3.22328Z"
                        fill="#1F2937"
                      />
                    </svg>
                  </div>
                  <div className="bg-white p-2 rounded shadow-md border border-gray-300">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M0.666016 3.16406C0.666016 1.78335 1.7853 0.664062 3.16602 0.664062H5.89872C6.6161 0.664062 7.253 1.12311 7.47986 1.80368L8.72803 5.54822C8.99032 6.33507 8.6341 7.19505 7.89225 7.56597L6.7618 8.1312C7.50064 9.42172 8.57502 10.4961 9.86554 11.235L10.4308 10.1045C10.8017 9.36264 11.6617 9.00643 12.4485 9.26871L16.1931 10.5169C16.8736 10.7437 17.3327 11.3806 17.3327 12.098V14.8307C17.3327 16.2114 16.2134 17.3307 14.8327 17.3307H13.9993C6.63555 17.3307 0.666016 11.3612 0.666016 3.9974V3.16406ZM3.16602 2.33073C2.70578 2.33073 2.33268 2.70383 2.33268 3.16406V3.9974C2.33268 10.4407 7.55603 15.6641 13.9993 15.6641H14.8327C15.2929 15.6641 15.666 15.291 15.666 14.8307V12.098L11.9215 10.8499L10.981 12.7309C10.7809 13.1309 10.3008 13.3017 9.89308 13.1179C7.67054 12.1158 5.8809 10.3262 4.87883 8.10366C4.695 7.69594 4.86581 7.2158 5.26584 7.01579L7.14689 6.07526L5.89872 2.33073H3.16602Z"
                        fill="#1F2937"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {speciesData?.map((species, index) => (
            <div key={index}>
              <div className="border rounded-md shadow-sm mb-3 mt-4">
                <button
                  type="button"
                  onClick={() => toggleAccordion(index)}
                  className="w-full flex justify-between items-center px-4 py-3 font-semibold text-lg"
                >
                  <span>{species.speciesName}</span>
                  <FontAwesomeIcon
                    className="text-[#000A16]"
                    icon={
                      openAccordions.includes(index)
                        ? faChevronUp
                        : faChevronDown
                    }
                  />
                </button>
                {openAccordions.includes(index) && (
                  <div className="p-4">
                    {/* Summary */}
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Summary</h2>
                      </div>
                      {/* Check-in */}
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold ml-2">Check-In</h3>
                      </div>
                      <div className="mt-4 p-3 rounded-lg shadow-md bg-quaternary">
                        <div className="bg-white p-4 rounded-md border mb-2">
                          <div className="flex w-full justify-start">
                            <div className="w-1/2">
                              <span className="block mb-2 text-gray-500 font-medium">
                                Length
                              </span>
                              <span>{species.checkIn.length}</span>
                            </div>
                            <div className="w-1/2">
                              <span className="block mb-2 text-gray-500 font-medium">
                                Weight
                              </span>
                              <span>{species.checkIn.weight}</span>
                            </div>
                          </div>
                          <div className="flex w-full justify-start mt-2">
                            <div className="w-1/2">
                              <p className="mb-2 text-gray-500">State</p>
                              <span>
                                {
                                  states.find(
                                    (state) =>
                                      state.id === species?.checkIn?.stateId,
                                  )?.stateName
                                }
                              </span>
                            </div>
                            <div className="w-1/2">
                              <p className="mb-2 text-gray-500">
                                Permit Number
                              </p>
                              <span>{species.checkIn.permitNumber || '-'}</span>
                            </div>
                          </div>
                          <div className="flex w-full justify-start mt-2">
                            <div className="w-1/2">
                              <p className="block mb-2 text-gray-500">
                                CITES Number
                              </p>
                              <span>{species.checkIn.citesNumber || '-'}</span>
                            </div>
                            <div className="w-1/2">
                              <p className="block mb-2 text-gray-500">
                                Confirmation Number
                              </p>
                              <span>
                                {species.checkIn.conformationNumber || '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                        {species?.checkIn?.notes && (
                          <div className="bg-white p-4 rounded-md border mb-2 mt-4">
                            <h4 className="font-medium mb-2">
                              Check - in notes:
                            </h4>
                            <div className="space-y-2">
                              <span className="text-[#43474E]">
                                {species.checkIn.notes}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Services Section */}
                    {!species?.services?.length ? (
                      <div className="mt-3">
                        <h3 className="text-lg font-semibold ml-2 mb-4">
                          Services
                        </h3>
                        <div className="bg-quaternary p-6 rounded-lg shadow-sm border text-center">
                          <h3 className="text-lg font-medium text-[#1C1B1F] mb-2">
                            No service selected! Go back and add services
                          </h3>
                          <div className="relative">
                            <button
                              type="button"
                              className="text-primary font-bold"
                            >
                              Select Service
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-semibold ml-2">
                            Services
                          </h3>
                        </div>
                        <div className="mt-3">
                          {species?.services?.map((service) => {
                            const master = services.find(
                              (m) => m.id === service.serviceId,
                            );
                            if (!master) return null;

                            return (
                              <div
                                key={service.serviceId}
                                className="p-4 rounded-md border bg-quaternary mb-4"
                              >
                                <div className="flex gap-4 items-start">
                                  {/* Image */}
                                  <img
                                    src={currentSpecies?.imageUrl}
                                    alt={currentSpecies?.name}
                                    className="w-16 h-16 object-cover rounded-md"
                                  />

                                  {/* Main content */}
                                  <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                      {/* Service Name */}
                                      <p className="text-gray-900 font-semibold text-base">
                                        {master.name}
                                      </p>
                                      {/* Rate */}
                                      <p className="text-gray-900 font-semibold text-sm whitespace-nowrap">
                                        ${master.rate}/{master.unit}
                                      </p>
                                    </div>
                                    <div className="bg-white p-2 mt-2 rounded-md border text-gray-600 text-sm w-fit">
                                      Total Price to be calculated after
                                      processing
                                    </div>

                                    {/* Deposit */}
                                    {service.depositAmount && (
                                      <div className="bg-[#F5F5F5] p-2 mt-2 rounded-md border flex justify-between items-center text-sm text-gray-600">
                                        <span className="font-semibold">
                                          Deposit Collected
                                        </span>
                                        <span className="text-gray-800 font-semibold">
                                          ${service.depositAmount}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {/* Products and Packaging */}
                    {!species?.products?.length ? (
                      <div className="mt-3">
                        <h3 className="text-lg font-semibold ml-2 mb-4">
                          Products and Packaging
                        </h3>
                        <div className="bg-quaternary p-6 rounded-lg shadow-sm border text-center">
                          <h3 className="text-lg font-medium text-[#1C1B1F] mb-2">
                            No products selected! Go back and add products
                          </h3>
                          <div className="relative">
                            <button
                              type="button"
                              className="text-primary font-bold"
                            >
                              Select Products
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-lg">
                        <div>
                          <h3 className="text-lg font-semibold ml-2 mb-4">
                            Products and Packaging
                          </h3>
                          <div className="bg-quaternary p-2 rounded-lg">
                            {species?.products?.map((product) => {
                              const master = masterProducts.find(
                                (p) => p.id === product.productId,
                              );
                              return (
                                <div key={product.productId} className="mb-4">
                                  <div className="p-4">
                                    <div className="flex justify-between items-baseline mb-1">
                                      <h4 className="font-semibold text-lg">
                                        {master?.name}
                                      </h4>
                                      {master && (
                                        <span className="text-gray-900 font-bold text-xl">
                                          ${' '}
                                          {calculateTotalPrice(
                                            product.quantity,
                                            product.addOnIds,
                                            product.packaging.map(
                                              (productPackage) => ({
                                                productPackageTypeId:
                                                  productPackage.productPackageTypeId,
                                                packingPercentage:
                                                  productPackage.percentage,
                                              }),
                                            ),
                                            master,
                                            addons,
                                            packageMasters,
                                          ).toFixed(2)}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex justify-between items-baseline">
                                      <p className="text-gray-600">
                                        Total Quantity: {product.quantity}
                                      </p>
                                      <span className="text-gray-800">
                                        ${master?.price}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Add-ons */}
                                  {product.addOnIds?.length > 0 && (
                                    <div className="bg-white p-4 rounded-md border mb-4">
                                      <h4 className="font-medium mb-2">
                                        Add-ons:{' '}
                                      </h4>
                                      <div className="space-y-2">
                                        {product.addOnIds.map((addOnId) => {
                                          const matchedAddon = addons.find(
                                            (addon) => addon.id === addOnId,
                                          );
                                          return (
                                            <div
                                              key={addOnId}
                                              className="flex justify-between"
                                            >
                                              <span className="w-1/2">
                                                {matchedAddon?.name}
                                              </span>
                                              <span className="w-1/2 text-right">
                                                Price: $
                                                {matchedAddon?.price?.toFixed(
                                                  2,
                                                ) ?? '0.00'}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  {/* Packaging */}
                                  {product.packaging?.length > 0 && (
                                    <div className="bg-white p-4 rounded-md border mb-4">
                                      <h4 className="font-medium mb-2 text-[#43474E]">
                                        Packaging:
                                      </h4>
                                      <div className="space-y-2">
                                        {product.packaging.map((pack) => {
                                          const matchedPackage =
                                            packageMasters.find(
                                              (pkg) =>
                                                pkg.id ===
                                                pack.productPackageTypeId,
                                            );

                                          return (
                                            <div
                                              key={pack.productPackageTypeId}
                                              className="flex justify-between text-sm text-gray-700"
                                            >
                                              <span className="w-1/4">
                                                {matchedPackage?.packageType}
                                              </span>
                                              <span className="w-1/4 text-center">
                                                Size:{' '}
                                                {matchedPackage?.packageSize}{' '}
                                                {WeightType.LBS}
                                              </span>
                                              {product.packaging.length > 1 ? (
                                                <span className="w-1/4 text-center">
                                                  Pkg Split:
                                                </span>
                                              ) : (
                                                <span className="w-1/4"></span>
                                              )}
                                              <span className="w-1/4 text-right">
                                                Price: $
                                                {matchedPackage?.price?.toFixed(
                                                  2,
                                                )}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}

                                  {/* Packaging Notes */}
                                  {product.packagingNotes && (
                                    <div className="bg-white p-4 rounded-md border mb-4">
                                      <h4 className="font-medium mb-2">
                                        Packaging Notes:{' '}
                                      </h4>
                                      <div className="space-y-2">
                                        <span className="text-[#43474E]">
                                          {product.packagingNotes}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Additional Notes */}
                    <div className="mt-2">
                      <div className="flex justify-between items-center mb-2 mt-3">
                        <h3 className="text-lg font-semibold ml-2">
                          Additional Notes
                        </h3>
                      </div>
                      <textarea
                        placeholder="Add notes or description..."
                        className="w-full border p-2 rounded-md h-25 focus:outline-primary"
                        value={additionalNotes[index] || ''}
                        maxLength={100}
                        readOnly
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="border rounded-md shadow-sm p-1 bg-[#F5F5F5] mt-3">
            <div className="flex justify-between items-center bg-[#F5F5F5] ">
              <span className="text-[#1C1B1F] font-medium">
                Estimated completion date
              </span>
              <span className="font-semibold text-[#43474E] py-2 px-3 min-w-[100px] inline-block text-center">
                {estCompletionDate}
              </span>
            </div>
          </div>
          {/* Advance Payment */}
          <div className="border rounded-md shadow-sm p-2 bg-quaternary mt-3">
            <div className="flex justify-between items-center bg-quaternary ">
              <span className="text-[#1C1B1F] font-medium">
                Total Advance Payment Made:
              </span>
              <span className="font-semibold text-[#43474E] py-2 px-3 text-lg min-w-[100px] inline-block text-center">
                ${advancePayment ?? 0}
              </span>
            </div>
          </div>
          <div className="border rounded-md shadow-sm p-2 bg-quaternary mt-3">
            <div className="flex justify-between items-center bg-quaternary ">
              <span className="text-[#1C1B1F] font-medium">
                Total Deposits Made:
              </span>
              <span className="font-semibold text-[#43474E] py-2 px-3 text-lg min-w-[100px] inline-block text-center">
                ${totalDeposit ?? 0}
              </span>
            </div>
          </div>
        </div>
        <div className="flex justify-between gap-4 fixed bottom-0 left-14 w-[calc(100%-64px)] p-4 ">
          <button
            onClick={() => handleNavigate(FrontendRoutes.CUSTOMER_LIST)}
            type="button"
            className="w-[50%] px-6 py-2 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Cancel
          </button>
          <button
            onClick={() => handleNavigate(FrontendRoutes.ALL_ORDER_LIST)}
            type="button"
            className="w-[50%] px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
          >
            Go to All Orders
          </button>
        </div>
      </div>
    </div>
  );
};
